import SiteShell from "../components/SiteShell";
import DiagramFlow from "../components/DiagramFlow";

const softwareCards = [
  {
    title: "Encrypted Data Channels",
    text: "Sensitive personal and salary information is protected with banking-grade encryption across all transactions.",
    image: "https://images.unsplash.com/photo-1584433144859-1fc3ab64a957",
  },
  {
    title: "Secure KYC Controls",
    text: "Identity verification, consent capture and audit trails are enforced with policy-based validations.",
    image: "https://images.unsplash.com/photo-1556740738-b6a63e27c4df",
  },
  {
    title: "Risk and Compliance Monitoring",
    text: "Every journey step is logged and monitored for compliance, fraud protection and regulatory readiness.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
  },
];

function Software() {
  return (
    <SiteShell
      route="software"
      heroHidden={true}
    >
      <section className="relative overflow-hidden bg-[#061a2e] pb-12 pt-24 text-white">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-[#0078b333] to-transparent blur-3xl opacity-50" />
          <div className="absolute top-0 left-1/4 h-px w-1/2 bg-gradient-to-r from-transparent via-sky-400/50 to-transparent" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1280px] px-6 md:px-10 lg:grid-cols-[1.2fr_0.8fr] grid items-center gap-16">
          <div>
            <span className="inline-flex rounded-full bg-white/10 px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.25em] text-sky-300 ring-1 ring-white/20 backdrop-blur-md">
              Fortified Tech
            </span>
            <h1 className="mt-8 text-4xl font-extrabold leading-tight tracking-tight md:text-7xl">
              Banking-Grade <br />Security at Scale
            </h1>
            <p className="mt-8 max-w-xl text-lg text-sky-100/80 leading-relaxed font-medium">
              Our architecture is built on a foundation of trust. We employ multi-layered security protocols to ensure every transaction is fortified and private.
            </p>
            {/* <div className="mt-12 flex flex-wrap gap-6 text-[11px] font-black uppercase tracking-widest">
              <a href="#/company" className="rounded-full bg-sky-500 px-10 py-5 text-white shadow-[0_10px_30px_-10px_rgba(14,165,233,0.5)] transition-all hover:-translate-y-1 hover:bg-sky-400">Security Whitepaper</a>
              <a href="#/banking" className="rounded-full border border-white/20 bg-white/5 px-10 py-5 text-white backdrop-blur-md transition-all hover:bg-white/10">Workflow Logic</a>
            </div> */}
          </div>
          <div className="relative p-2 rounded-[40px] bg-gradient-to-br from-white/10 to-transparent ring-1 ring-white/20 backdrop-blur-2xl">
            <img src="https://images.unsplash.com/photo-1558346490-a72e53ae2d4f" alt="Software stack" className="relative rounded-[32px] shadow-2xl grayscale brightness-125 mix-blend-screen" />
          </div>
        </div>
      </section>

      <DiagramFlow
        title="Institutional Security Controls"
        subtitle="Security isn't an afterthought; it's integrated into every node of the instant personal loan journey."
        steps={[
          { title: "Identity Shield", text: "Multi-factor authentication and biometric readiness." },
          { title: "Data Fortress", text: "End-to-end AES-256 encryption for all data packets." },
          { title: "Guardrails", text: "Rule-based policy execution engine for compliance." },
          { title: "Watchtower", text: "24/7 anomaly detection and real-time audit logging." },
        ]}
      />

      <section className="mx-auto max-w-[1280px] px-6 py-20 md:px-10">
        <h2 className="text-3xl font-extrabold tracking-tight text-[#0a2c4f] md:text-4xl text-center mb-16">Security Architecture</h2>
        <div className="grid gap-8 md:grid-cols-3">
          {softwareCards.map((card) => (
            <article
              key={card.title}
              className="group relative overflow-hidden rounded-[32px] border border-slate-100 bg-white p-2 shadow-xl transition-all duration-500 hover:-translate-y-2"
            >
              <div className="relative h-56 overflow-hidden rounded-[24px]">
                <img src={card.image} alt={card.title} className="h-full w-full object-cover transition duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/30 to-transparent" />
              </div>
              <div className="p-8">
                <h3 className="text-xl font-bold tracking-tight text-[#0a2c4f]">{card.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-500">{card.text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-6 pb-24 md:px-10">
        <div className="grid gap-12 overflow-hidden rounded-[48px] border border-slate-200 bg-white shadow-2xl md:grid-cols-[1.1fr_0.9fr]">
          <div className="p-10 md:p-16">
            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#0078b3]">
              Infrastructure
            </p>
            <h2 className="mt-6 text-3xl font-extrabold tracking-tight text-[#0a2c4f] md:text-4xl">
              Deep-Tech Data Protection
            </h2>
            <div className="mt-12 space-y-4">
              {[
                { title: "Interface Security", desc: "Controlled user sessions & secure input handling." },
                { title: "Validation Engine", desc: "Automated KYC and salary verification protocols." },
                { title: "Compliance Core", desc: "Tamper-proof audit logs and regulatory reporting." }
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-5 p-6 rounded-2xl bg-slate-50 transition-colors hover:bg-white hover:shadow-lg">
                  <div className="mt-1 h-3 w-3 rounded-full bg-[#0078b3] shadow-[0_0_10px_#0078b366]" />
                  <div>
                    <p className="font-bold text-[#0a2c4f]">{item.title}</p>
                    <p className="mt-1 text-sm text-slate-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative bg-[#061a2e] p-6 lg:p-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,#0078b333_0%,transparent_70%)]" />
            <img
              src="/security_encryption_concept_1772861359511.png"
              alt="Security operations visualization"
              className="relative h-full w-full rounded-3xl object-cover opacity-80"
            />
          </div>
        </div>
      </section>
    </SiteShell>
  );
}

export default Software;
